// src/pages/sessions/components/LiveSession/EnhancedParticipantsList.jsx
import React, { useEffect, useState, useRef, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParticipants } from '@livekit/components-react';
import { ConnectionQuality } from 'livekit-client';
import { UsersIcon, PlusIcon, VideoCameraIcon, SpeakerXMarkIcon, SignalSlashIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// Time in ms that poor quality must persist before showing warning (5 seconds)
const POOR_QUALITY_DEBOUNCE_MS = 5000;

// Cleanup utility for EnhancedParticipantsList
class ParticipantsListCleanup {
  constructor() {
    this.timeouts = new Set();
    this.disposed = false;
  }

  setTimeout(fn, delay) {
    if (this.disposed) return null;
    const id = setTimeout(() => {
      this.timeouts.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timeouts.add(id);
    return id;
  }

  clearTimeout(id) {
    if (id) {
      clearTimeout(id);
      this.timeouts.delete(id);
    }
  }

  cleanup() {
    this.disposed = true;
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts.clear();
  }
}

// Connection status indicator component
const ConnectionIndicator = memo(({ connectionQuality, isUnstable, t }) => {
  const isLost = connectionQuality === ConnectionQuality.Lost;
  const showUnstable = isUnstable && !isLost;
  
  if (isLost) {
    return (
      <span 
        className="inline-flex items-center gap-1.5 text-xs bg-red-600 text-white font-semibold px-2 py-1 rounded"
        title={t('liveSession.connection.participantLost', 'Connection lost')}
      >
        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
        <span className="hidden sm:inline">{t('liveSession.connection.reconnecting', 'Reconnecting...')}</span>
        <span className="sm:hidden">{t('liveSession.connection.lost', 'Lost')}</span>
      </span>
    );
  }
  
  if (showUnstable) {
    return (
      <span 
        className="inline-flex items-center gap-1 text-xs bg-yellow-500 text-white font-semibold px-2 py-1 rounded"
        title={t('liveSession.connection.participantPoor', 'Unstable connection')}
      >
        <ExclamationTriangleIcon className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{t('liveSession.connection.unstable', 'Unstable')}</span>
      </span>
    );
  }
  
  return null;
});

ConnectionIndicator.displayName = 'ConnectionIndicator';

// ParticipantItem with debounced poor quality detection
const ParticipantItem = memo(({
  data,
  virtualRooms,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  isTeacher
}) => {
  const { t } = useTranslation();
  const [isUnstable, setIsUnstable] = useState(false);
  const poorQualityTimerRef = useRef(null);
  const prevQualityRef = useRef(data.connectionQuality);
  
  const isTeacherRole = data.role === 'teacher';
  const assignedRoom = virtualRooms.find(r => r.id === data.virtualRoomId);
  const isBroadcastingTo = data.virtualRoomId && teacherBroadcastRooms.has(data.virtualRoomId);
  const isMonitoringRoom = data.virtualRoomId && teacherMonitorRooms.has(data.virtualRoomId);
  const isSilentMonitoringRoom = data.virtualRoomId && teacherMonitorSilentRooms.has(data.virtualRoomId);
  
  const isLostConnection = data.connectionQuality === ConnectionQuality.Lost;
  
  // Handle connection quality changes with debounce
  useEffect(() => {
    const quality = data.connectionQuality;
    
    if (quality === ConnectionQuality.Poor) {
      // Start timer if not already running
      if (!poorQualityTimerRef.current) {
        poorQualityTimerRef.current = setTimeout(() => {
          setIsUnstable(true);
          poorQualityTimerRef.current = null;
        }, POOR_QUALITY_DEBOUNCE_MS);
      }
    } else {
      // Quality changed from Poor - clear timer and reset unstable state
      if (poorQualityTimerRef.current) {
        clearTimeout(poorQualityTimerRef.current);
        poorQualityTimerRef.current = null;
      }
      // Only clear unstable if quality improved (not if it became Lost)
      if (quality !== ConnectionQuality.Lost) {
        setIsUnstable(false);
      }
    }
    
    prevQualityRef.current = quality;
    
    return () => {
      if (poorQualityTimerRef.current) {
        clearTimeout(poorQualityTimerRef.current);
        poorQualityTimerRef.current = null;
      }
    };
  }, [data.connectionQuality]);

  // Determine border color based on connection status
  const getBorderClass = () => {
    if (isLostConnection) return 'ring-1 ring-red-500';
    if (isUnstable) return 'ring-1 ring-yellow-500';
    return '';
  };

  return (
    <div className="mb-2">
      <div className={`flex items-center justify-between p-2 bg-gray-700 rounded hover:bg-gray-600 transition-colors ${getBorderClass()}`}>
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {/* Name with truncation */}
          <span className="text-sm text-white font-medium truncate max-w-[120px] sm:max-w-[160px]">
            {data.name}
          </span>
          
          {/* Connection status indicator */}
          <ConnectionIndicator 
            connectionQuality={data.connectionQuality}
            isUnstable={isUnstable}
            t={t}
          />
          
          {isTeacherRole && (
            <span className="text-xs bg-cyan-600 text-white font-semibold px-2 py-1 rounded whitespace-nowrap">
              {t('roles.teacher')}
            </span>
          )}
          
          {assignedRoom ? (
            <span className="text-xs bg-purple-600 text-white font-semibold px-2 py-1 rounded whitespace-nowrap truncate max-w-[100px]">
              {assignedRoom.display_name || assignedRoom.room_name || t('videoGrid.room', { number: assignedRoom.room_number })}
            </span>
          ) : !isTeacherRole && (
            <span className="text-xs bg-blue-600 text-white font-semibold px-2 py-1 rounded whitespace-nowrap">
              {t('liveSession.participants.mainRoom')}
            </span>
          )}

          {isBroadcastingTo && isTeacher && (
             <span 
               className="p-1 bg-cyan-600 rounded flex items-center justify-center flex-shrink-0" 
               title={t('liveSession.participants.joinedRoom')}
             >
               <PlusIcon className="w-3.5 h-3.5 text-white stroke-2" />
             </span>
          )}
          
          {isMonitoringRoom && isTeacher && (
            <span 
              className="p-1 bg-purple-600 rounded flex items-center justify-center flex-shrink-0" 
              title={t('liveSession.participants.monitoring')}
            >
              <VideoCameraIcon className="w-3.5 h-3.5 text-white stroke-2" />
            </span>
          )}
          
          {isSilentMonitoringRoom && isTeacher && (
            <span 
              className="p-1 bg-blue-600 rounded flex items-center justify-center flex-shrink-0" 
              title={t('liveSession.participants.silentMonitoring')}
            >
              <SpeakerXMarkIcon className="w-3.5 h-3.5 text-white stroke-2" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Optimized comparison for ParticipantItem
  if (prevProps.data.identity !== nextProps.data.identity ||
      prevProps.data.name !== nextProps.data.name ||
      prevProps.data.role !== nextProps.data.role ||
      prevProps.data.virtualRoomId !== nextProps.data.virtualRoomId ||
      prevProps.data.connectionQuality !== nextProps.data.connectionQuality ||
      prevProps.isTeacher !== nextProps.isTeacher) {
    return false; // Re-render needed
  }

  // Check broadcast/monitor status only if room exists
  const roomId = prevProps.data.virtualRoomId;
  if (roomId) {
    if (prevProps.teacherBroadcastRooms.has(roomId) !== nextProps.teacherBroadcastRooms.has(roomId) ||
        prevProps.teacherMonitorRooms.has(roomId) !== nextProps.teacherMonitorRooms.has(roomId) ||
        prevProps.teacherMonitorSilentRooms.has(roomId) !== nextProps.teacherMonitorSilentRooms.has(roomId)) {
      return false; // Re-render needed
    }
  }

  // Check if virtualRooms array reference changed (for room name updates)
  if (prevProps.virtualRooms !== nextProps.virtualRooms) {
    const prevRoom = prevProps.virtualRooms.find(r => r.id === roomId);
    const nextRoom = nextProps.virtualRooms.find(r => r.id === roomId);
    if (prevRoom?.display_name !== nextRoom?.display_name ||
        prevRoom?.room_name !== nextRoom?.room_name ||
        prevRoom?.room_number !== nextRoom?.room_number) {
      return false; // Re-render needed
    }
  }

  return true; // Skip re-render
});

ParticipantItem.displayName = 'ParticipantItem';

const EnhancedParticipantsList = ({
  participantVirtualRooms,
  virtualRooms,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  isTeacher
}) => {
  const { t } = useTranslation();
  const liveParticipants = useParticipants();
  const [stableParticipants, setStableParticipants] = useState([]);

  // Refs for tracking state
  const cleanupRef = useRef(new ParticipantsListCleanup());
  const cleanup = cleanupRef.current;
  const mountedRef = useRef(true);
  const previousParticipantsRef = useRef([]);
  const updateTimeoutRef = useRef(null);

  // Parse participant metadata safely
  const parseMetadata = useCallback((participant) => {
    let metadata = {};
    if (participant?.metadata) {
      try {
        metadata = JSON.parse(participant.metadata);
      } catch (e) {
        // Silently handle parse errors
        console.debug('Failed to parse participant metadata:', e);
      }
    }
    return metadata;
  }, []);

  // Process participants list
  const processParticipants = useCallback(() => {
    if (!mountedRef.current) return;

    const newList = [];

    liveParticipants.forEach(participant => {
      const existingParticipant = previousParticipantsRef.current.find(
        p => p.identity === participant.identity
      );

      const metadata = parseMetadata(participant);

      const participantData = {
        identity: participant.identity,
        name: metadata.displayName || participant.name || participant.identity || t('videoGrid.unknown'),
        role: metadata.role,
        virtualRoomId: metadata.virtualRoomId !== undefined ?
          metadata.virtualRoomId :
          participantVirtualRooms[participant.identity],
        connectionQuality: participant.connectionQuality
      };

      // Only add if data changed or is new
      if (!existingParticipant ||
          existingParticipant.name !== participantData.name ||
          existingParticipant.role !== participantData.role ||
          existingParticipant.virtualRoomId !== participantData.virtualRoomId ||
          existingParticipant.connectionQuality !== participantData.connectionQuality) {
        newList.push(participantData);
      } else {
        newList.push(existingParticipant);
      }
    });

    // Only update state if data actually changed
    const newListStr = JSON.stringify(newList);
    const prevListStr = JSON.stringify(previousParticipantsRef.current);

    if (newListStr !== prevListStr) {
      previousParticipantsRef.current = newList;
      if (mountedRef.current) {
        setStableParticipants(newList);
      }
    }
  }, [liveParticipants, participantVirtualRooms, parseMetadata, t]);

  // Debounced update effect
  useEffect(() => {
    // Clear any existing timeout
    if (updateTimeoutRef.current) {
      cleanup.clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }

    // Set new timeout for debounced update
    updateTimeoutRef.current = cleanup.setTimeout(() => {
      processParticipants();
      updateTimeoutRef.current = null;
    }, 500);

    return () => {
      if (updateTimeoutRef.current) {
        cleanup.clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
  }, [liveParticipants, participantVirtualRooms, processParticipants, cleanup]);

  // Force update when teacher broadcast/monitor rooms change
  useEffect(() => {
    // This triggers re-render of ParticipantItem components with new props
    // The memo comparison will handle whether each item actually needs to re-render
  }, [teacherBroadcastRooms.size, teacherMonitorRooms.size, teacherMonitorSilentRooms.size]);

  // Track mounted state and cleanup
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      cleanup.cleanup();
      previousParticipantsRef.current = [];
    };
  }, [cleanup]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        {stableParticipants.length > 0 ? (
          stableParticipants.map(data => (
            <ParticipantItem
              key={data.identity}
              data={data}
              virtualRooms={virtualRooms}
              teacherBroadcastRooms={teacherBroadcastRooms}
              teacherMonitorRooms={teacherMonitorRooms}
              teacherMonitorSilentRooms={teacherMonitorSilentRooms}
              isTeacher={isTeacher}
            />
          ))
        ) : (
          <div className="text-center py-8 text-gray-400">
            <UsersIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t('liveSession.participants.noParticipants')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Export with custom memo comparison for the main component
export default memo(EnhancedParticipantsList, (prevProps, nextProps) => {
  // Check if isTeacher changed
  if (prevProps.isTeacher !== nextProps.isTeacher) {
    return false; // Re-render needed
  }

  // Deep comparison for participantVirtualRooms object
  const prevRoomsStr = JSON.stringify(prevProps.participantVirtualRooms);
  const nextRoomsStr = JSON.stringify(nextProps.participantVirtualRooms);
  if (prevRoomsStr !== nextRoomsStr) {
    return false; // Re-render needed
  }

  // Check if virtualRooms array changed (reference or content)
  if (prevProps.virtualRooms !== nextProps.virtualRooms) {
    // Check if array length changed
    if (prevProps.virtualRooms.length !== nextProps.virtualRooms.length) {
      return false; // Re-render needed
    }
    
    // Check if any room data changed
    for (let i = 0; i < prevProps.virtualRooms.length; i++) {
      const prevRoom = prevProps.virtualRooms[i];
      const nextRoom = nextProps.virtualRooms[i];
      
      if (!prevRoom || !nextRoom) {
        return false; // Re-render needed
      }
      
      if (prevRoom.id !== nextRoom.id ||
          prevRoom.display_name !== nextRoom.display_name ||
          prevRoom.room_name !== nextRoom.room_name ||
          prevRoom.room_number !== nextRoom.room_number) {
        return false; // Re-render needed
      }
    }
  }

  // Check if teacherBroadcastRooms Set changed
  if (prevProps.teacherBroadcastRooms !== nextProps.teacherBroadcastRooms) {
    if (prevProps.teacherBroadcastRooms.size !== nextProps.teacherBroadcastRooms.size) {
      return false; // Re-render needed
    }
    
    // Check if Set contents changed
    for (const roomId of prevProps.teacherBroadcastRooms) {
      if (!nextProps.teacherBroadcastRooms.has(roomId)) {
        return false; // Re-render needed
      }
    }
  }

  // Check if teacherMonitorRooms Set changed
  if (prevProps.teacherMonitorRooms !== nextProps.teacherMonitorRooms) {
    if (prevProps.teacherMonitorRooms.size !== nextProps.teacherMonitorRooms.size) {
      return false; // Re-render needed
    }
    
    // Check if Set contents changed
    for (const roomId of prevProps.teacherMonitorRooms) {
      if (!nextProps.teacherMonitorRooms.has(roomId)) {
        return false; // Re-render needed
      }
    }
  }


  // Check if teacherMonitorSilentRooms Set changed
  if (prevProps.teacherMonitorSilentRooms !== nextProps.teacherMonitorSilentRooms) {
    if (prevProps.teacherMonitorSilentRooms.size !== nextProps.teacherMonitorSilentRooms.size) {
      return false; // Re-render needed
    }
    
    // Check if Set contents changed
    for (const roomId of prevProps.teacherMonitorSilentRooms) {
      if (!nextProps.teacherMonitorSilentRooms.has(roomId)) {
        return false; // Re-render needed
      }
    }
  }
  return true; // Skip re-render - props are the same
});